"use client"

import React, { useEffect } from "react"
import { ThemeProvider as NextThemesProvider } from "next-themes"
import type { ThemeProviderProps } from "next-themes/dist/types"
import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"

import { getCookieConsent } from "@/lib/zaraz-cookie-consent"
import { TooltipProvider } from "@/components/ui/tooltip"
// PostHog
import SuspendedPostHogPageView from "@/app/PostHogPageView"

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider {...props}>
      <TooltipProvider>{children}</TooltipProvider>
    </NextThemesProvider>
  )
}

export type AdditonalTranslationsProps =
  | {
      translatedSubject: string
      translatedCategory: string
      translatedSubcategory: string
    }
  | undefined

const AdditonalTranslationsContext =
  React.createContext<AdditonalTranslationsProps>(undefined)

export function useAdditionalTranslations() {
  const context = React.useContext(AdditonalTranslationsContext)
  // guard must be wrapped in <AdditionalTranslationsProvider>
  if (context === undefined) {
    throw new Error(
      "useAdditionalTranslations must be used within a AdditionalTranslationsProvider"
    )
  }

  return context
}

export function AdditionalTranslationsProvider({
  children,
  additionalMessages,
}: {
  children: React.ReactNode
  additionalMessages: AdditonalTranslationsProps
}) {
  return (
    <AdditonalTranslationsContext.Provider value={additionalMessages}>
      {children}
    </AdditonalTranslationsContext.Provider>
  )
}

export function PHProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Guard clause: check if we're on the correct domain
    //  if (typeof window !== "undefined" && window.location.hostname !== "uniquenamegenerators.com") {
    //     return
    //  }
    // Check if in development mode
    if (process.env.NODE_ENV === "development") {
      return
    }

    const { success: userConsentedToAnalytics } = getCookieConsent("analytics")

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: "/ingest",
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true, // Enable pageleave capture
      persistence: userConsentedToAnalytics ? "localStorage+cookie" : "memory",
    })
  }, [])

  // Check if in development mode
  if (process.env.NODE_ENV === "development") {
    return <>{children}</>
  }

  // Return children directly if not on uniquenamegenerators.com
  // if (typeof window !== "undefined" && window.location.hostname !== "uniquenamegenerators.com") {
  //    return children
  // }

  return (
    <PostHogProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PostHogProvider>
  )
}
